import type { Directive, DirectiveBinding } from 'vue'
import { tracker } from '@/plugins/event-tracker'

interface TrackElement extends HTMLElement {
  _trackHandler?: EventListener
  _trackEventType?: string
}

// 埋点配置类型
export interface TrackConfig {
  event: string // 事件名称
  params?: Record<string, unknown> // 事件参数
  immediate?: boolean // 是否立即上报（不等待防抖）
}

/**
 * 埋点指令
 * @example
 * // 基础用法
 * <button v-track="'button_click'">点击</button>
 *
 * // 带参数
 * <button v-track="{ event: 'button_click', params: { name: '提交' } }">点击</button>
 *
 * // 配合防抖使用（立即上报）
 * <button v-track="{ event: 'button_click', immediate: true }" v-debounce @click="handleClick">点击</button>
 *
 */
export const track: Directive = {
  mounted(el: TrackElement, binding: DirectiveBinding) {
    const eventType = binding.arg || 'click' // 默认监听 click 事件
    const { value } = binding

    if (!value) {
      console.warn('[v-track] 指令需要提供事件名称或配置对象')
      return
    }

    // 解析配置
    let eventName: string
    let params: Record<string, unknown> = {}
    let immediate = false

    if (typeof value === 'string') {
      eventName = value
    } else if (typeof value === 'object' && value.event) {
      eventName = value.event
      params = value.params || {}
      immediate = value.immediate ?? false
    } else {
      console.warn('[v-track] 指令参数格式错误')
      return
    }

    // 创建埋点处理器
    const handler = (event: Event) => {
      // 上报埋点
      tracker.event(eventName, params)
    }

    // 保存处理器
    el._trackHandler = handler
    el._trackEventType = eventType

    // 如果需要立即上报（不受防抖影响），在捕获阶段监听
    // 这样会在防抖指令之前执行
    if (immediate) {
      el.addEventListener(eventType, handler, true)
    } else {
      // 否则在冒泡阶段监听，与业务逻辑一起执行
      el.addEventListener(eventType, handler, false)
    }
  },

  updated(el: TrackElement, binding: DirectiveBinding) {
    const eventType = binding.arg || 'click'
    const { value, oldValue } = binding

    // 如果配置没有变化，不需要更新
    if (value === oldValue) {
      return
    }

    // 移除旧的监听器
    if (el._trackHandler && el._trackEventType) {
      el.removeEventListener(el._trackEventType, el._trackHandler, true)
      el.removeEventListener(el._trackEventType, el._trackHandler, false)
    }

    if (!value) {
      return
    }

    // 解析新配置
    let eventName: string
    let params: Record<string, unknown> = {}
    let immediate = false

    if (typeof value === 'string') {
      eventName = value
    } else if (typeof value === 'object' && value.event) {
      eventName = value.event
      params = value.params || {}
      immediate = value.immediate ?? false
    } else {
      return
    }

    // 创建新的埋点处理器
    const handler = (event: Event) => {
      tracker.event(eventName, params)
    }

    el._trackHandler = handler
    el._trackEventType = eventType

    if (immediate) {
      el.addEventListener(eventType, handler, true)
    } else {
      el.addEventListener(eventType, handler, false)
    }
  },

  unmounted(el: TrackElement) {
    // 移除事件监听器
    if (el._trackHandler && el._trackEventType) {
      el.removeEventListener(el._trackEventType, el._trackHandler, true)
      el.removeEventListener(el._trackEventType, el._trackHandler, false)
      delete el._trackHandler
      delete el._trackEventType
    }
  },
}
